import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps'
import { scaleQuantile } from 'd3-scale'
import { Button, CircularLoader, Icon, Modal } from '@st-operations/uikit/src/v2'
import classNames from 'classnames'
import format from '@st-operations/uikit/lib/format.js'

import countries from './countries.js'
import GeoLegend from '../geo-legend/index.jsx'
import { CALCULATION_METHODS } from '../../pages/event-feeds/visualization-panel/highlights/geo/index.jsx'

import './styles.scss'
import calculateTextWidth from '_lib/calculate-text-width.js'
import { Fragment } from 'react'

const GROUPING_KMS = 50
const BUBBLE_CUT_THRESHOLD_LESS_THAN = 0.01
const CITY_NAMES_CUT_ZOOM_LESS_THAN = 2

function twoDecimals(n) {
  let log10 = n ? Math.floor(Math.log10(n)) : 0,
    div = log10 < 0 ? Math.pow(10, 1 - log10) : 100

  return Math.round(n * div) / div
}

function showTooltip(evt, text) {
  let tooltip = document.getElementById('tooltip')
  tooltip.innerHTML = text
  tooltip.style.display = 'block'
  tooltip.style.left = evt.pageX + 30 + 'px'
  tooltip.style.top = evt.pageY + 30 + 'px'
}

function hideTooltip() {
  let tooltip = document.getElementById('tooltip')
  tooltip.style.display = 'none'
}

function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  let R = 6371 // Radius of the earth in km
  let dLat = deg2rad(lat2 - lat1) // deg2rad below
  let dLon = deg2rad(lon2 - lon1)
  let a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  let d = R * c // Distance in km
  return d
}

function deg2rad(deg) {
  return deg * (Math.PI / 180)
}

const CountryDistribution = ({
  className,
  calculationMethod = CALCULATION_METHODS.EVENTS,
  data = [],
  postalCodeData,
  loading,
  style,
  geographySelection,
  continents,
  projection,
  projectionConfig = {
    rotate: [-10, 0, 0],
    scale: 147,
  },
  viewport = '0 100 800 400',
  position = { coordinates: [0, 0], zoom: 1 },
  onPositionChange,
  hideZoomControls,
  disableClick,
  onBack = () => {},
  onGeographySelectionChanged = () => null,
}) => {
  let scoreKey
  if (calculationMethod === CALCULATION_METHODS.EVENTS) {
    scoreKey = 'size'
  } else if (calculationMethod === CALCULATION_METHODS.PER_CAPITA) {
    scoreKey = 'perCapitaScore'
  } else if (calculationMethod === CALCULATION_METHODS.INDEX) {
    scoreKey = 'indexScore'
  } else if (calculationMethod === CALCULATION_METHODS.UNIQUE_USERS) {
    scoreKey = 'users'
  }

  const containerRef = useRef()

  const min = 0
  const max = data.reduce((max, cur) => Math.max(max, cur[scoreKey]), 0)

  const minIndexNegative = data.reduce((min, cur) => Math.min(min, cur[scoreKey]), 0)
  const maxIndexPositive = data.reduce((acc, cur) => Math.max(acc, cur[scoreKey]), 0)

  const legendStart = calculationMethod === CALCULATION_METHODS.INDEX ? minIndexNegative : min
  const legendEnd = calculationMethod === CALCULATION_METHODS.INDEX ? maxIndexPositive : max

  const domain = data.map((geo) => geo[scoreKey])

  const colorScale = scaleQuantile()
    .domain(domain)
    .range([
      '#CBD2CD',
      '#B9C7BD',
      '#A6BDAD',
      '#94B29C',
      '#81A88C',
      '#6F9D7C',
      '#5D926C',
      '#4A885C',
      '#387D4B',
      '#25733B',
      '#13682B',
    ])
  const colorScaleIndexNegative = scaleQuantile()
    .domain(domain)
    .range([
      '#904172',
      '#984F7C',
      '#A15D87',
      '#A96B91',
      '#B1799C',
      '#B987A6',
      '#C296B1',
      '#CAA4BB',
      '#D2B2C6',
      '#DAC0D0',
      '#E3CEDB',
      '#EBDCE5',
    ])
  const colorScaleIndexPositive = scaleQuantile()
    .domain(domain)
    .range([
      '#CBD2CD',
      '#B9C7BD',
      '#A6BDAD',
      '#94B29C',
      '#81A88C',
      '#6F9D7C',
      '#5D926C',
      '#4A885C',
      '#387D4B',
      '#25733B',
      '#13682B',
    ])

  const [rendering, setRendering] = useState(false)
  const [geographyHover, setGeographyHover] = useState()

  useEffect(() => {
    setRendering(true)
    document.querySelector('.st-country-distribution svg').setAttribute('viewBox', viewport)
  }, [])

  const groupedPostalCodeData = useMemo(() => {
    const grouped = []
    Object.values(postalCodeData?.zipCodes || []).forEach((postalCode) => {
      let found = false
      for (const g of grouped) {
        const d = getDistanceFromLatLonInKm(g.lat, g.long, +postalCode.lat, +postalCode.long)
        if (d < GROUPING_KMS) {
          g.postalCodes.push(postalCode)
          g.lat = (g.lat + +postalCode.lat) / 2
          g.long = (g.long + +postalCode.long) / 2
          g.percent = g.percent + +postalCode.percent || 0
          g.size = g.size + +postalCode.size || 0
          found = true
          break
        }
      }
      if (!found) {
        const groupItem = {
          postalCodes: [postalCode],
          lat: +postalCode.lat,
          long: +postalCode.long,
          percent: +postalCodeData?.percent || 0,
          size: +postalCodeData?.size || 0,
        }
        grouped.push(groupItem)
      }
    })
    return grouped
  }, [postalCodeData])

  const minMaxPostalCodeEvents = [
    groupedPostalCodeData
      .map((pc) => pc.size)
      .reduce((min, cur) => Math.min(min, cur), Number.MAX_SAFE_INTEGER),
    groupedPostalCodeData
      .map((pc) => pc.size)
      .reduce((max, cur) => Math.max(max, cur), Number.MIN_SAFE_INTEGER),
  ]

  function handleZoomIn() {
    if (position.zoom >= 30) return
    onPositionChange({ ...position, zoom: position.zoom * 2 })
  }

  function handleZoomOut() {
    if (position.zoom <= 1) return
    onPositionChange({ ...position, zoom: position.zoom / 2 })
  }

  function handleMoveEnd(position) {
    onPositionChange(position)
  }

  function calculateArea(coords) {
    let area = 0

    for (let i = 0; i < coords.length; i++) {
      const [x1, y1] = coords[i]
      const [x2, y2] = coords[(i + 1) % coords.length]

      area += x1 * y2 - x2 * y1
    }

    return Math.abs(area) / 2
  }

  function calcGeographyTotalArea(geo) {
    const result = geo.geometry?.coordinates?.reduce(
      (totalArea, coords) =>
        totalArea + calculateArea(coords.length === 1 ? coords[0] : coords || []),
      0
    )
    return result
  }

  const [geoData, setGeoData] = useState({})

  useEffect(() => {
    async function fetchGeoData() {
      const file = '/static/geo/world-110m.json'
      const response = await (await fetch(file)).json()
      setGeoData(response)
    }

    fetchGeoData()
  }, [])

  return (
    <div
      ref={containerRef}
      className={classNames('st-country-distribution', className)}
      style={style}
    >
      <div id="tooltip" display="none" style={{ position: 'fixed', display: 'none' }} />
      <ComposableMap projection={projection} projectionConfig={projectionConfig}>
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          maxZoom={30}
        >
          <If condition={data.length > 0 && Object.keys(geoData).length !== 0}>
            <Geographies geography={geoData}>
              {({ geographies, projection, path }) => {
                return geographies
                  .sort((g1) =>
                    g1?.properties?.ISO_A2 === geographyHover?.properties?.ISO_A2 ||
                    g1?.properties?.ISO_A2 === geographySelection?.properties?.ISO_A2
                      ? 1
                      : -1
                  )
                  .map((geo, index) => {
                    const cur = data.find((s) => s.id === geo.properties.ISO_A2)
                    const hovered = geographyHover?.properties?.ISO_A2 === geo.properties.ISO_A2
                    const selected =
                      geo?.properties?.ISO_A2 === geographySelection?.properties?.ISO_A2
                    const area = calcGeographyTotalArea(geo)

                    if (index === geographies.length - 1) {
                      setRendering(false)
                    }

                    if (
                      !continents ||
                      continents.includes(countries[geo.properties.ISO_A2].continent) ||
                      continents.includes(countries[geo.properties.ISO_A2].region) ||
                      continents.includes(countries[geo.properties.ISO_A2].name)
                    )
                      return (
                        <Fragment key={geo.rsmKey}>
                          <Geography
                            geography={geo}
                            fill={
                              !geographySelection || selected
                                ? cur && cur[scoreKey] !== 1
                                  ? calculationMethod === CALCULATION_METHODS.INDEX
                                    ? cur[scoreKey] < 1
                                      ? colorScaleIndexNegative(cur[scoreKey])
                                      : colorScaleIndexPositive(cur[scoreKey])
                                    : colorScale(cur[scoreKey])
                                  : 'lightgray'
                                : 'lightgray'
                            }
                            strokeWidth={
                              geographySelection ? 0.5 : selected ? 0.5 : hovered ? 1 : 0.5
                            }
                            stroke={
                              geographySelection
                                ? 'white'
                                : selected
                                ? 'white'
                                : hovered
                                ? '#0a332b'
                                : 'white'
                            }
                            onMouseEnter={(evt) => {
                              if (
                                !disableClick &&
                                calculationMethod === CALCULATION_METHODS.EVENTS
                              ) {
                                setGeographyHover(geo)
                              }
                              showTooltip(evt, geo.properties.NAME)
                            }}
                            onMouseLeave={() => {
                              if (
                                !disableClick &&
                                calculationMethod === CALCULATION_METHODS.EVENTS
                              ) {
                                setGeographyHover(undefined)
                              }
                              hideTooltip()
                            }}
                            onClick={() => {
                              if (calculationMethod === CALCULATION_METHODS.EVENTS) {
                                const centroid = projection.invert(path.centroid(geo))
                                const [[x0, y0], [x1, y1]] = path.bounds(geo)

                                let zoom
                                if (geo.properties.ISO_A2 === 'US') {
                                  zoom = 4
                                } else {
                                  zoom =
                                    0.9 /
                                    Math.max(
                                      (x1 - x0) / containerRef.current.offsetWidth,
                                      (y1 - y0) / containerRef.current.offsetHeight
                                    )
                                }

                                onPositionChange({
                                  coordinates: centroid,
                                  zoom,
                                })
                                onGeographySelectionChanged(geo)
                              }
                            }}
                            cursor={
                              calculationMethod === CALCULATION_METHODS.EVENTS && !disableClick
                                ? 'pointer'
                                : undefined
                            }
                          />
                          {selected &&
                            groupedPostalCodeData?.map((item) => {
                              const minMaxRatio = area > 500 ? 1 : area < 50 ? 0.2 : 0.5
                              const minMaxRadius = [0.3 * minMaxRatio, 10 * minMaxRatio]
                              function calcRadiusByEvents(size) {
                                const min = minMaxPostalCodeEvents[0]
                                const max = minMaxPostalCodeEvents[1]
                                const range = max - min
                                const correctedStartValue = size - min
                                const percentage = correctedStartValue / range

                                const minRadius = minMaxRadius[0]
                                const maxRadius = minMaxRadius[1]
                                return minRadius + (maxRadius - minRadius) * percentage
                              }
                              if (item) {
                                return (
                                  <Marker
                                    key={item.postalCodes[0].id}
                                    coordinates={[item.long, item.lat]}
                                  >
                                    <If
                                      condition={
                                        item.size >
                                        (minMaxPostalCodeEvents[1] - minMaxPostalCodeEvents[0]) *
                                          BUBBLE_CUT_THRESHOLD_LESS_THAN
                                      }
                                    >
                                      <circle
                                        fill="#FFFFFF55"
                                        strokeWidth={0.1}
                                        stroke="#0e4c40"
                                        r={calcRadiusByEvents(item.size)}
                                      >
                                        <title>
                                          lat: {item.lat} long: {item.long} size: {item.size}{' '}
                                          percent: {item.percent} Postal Codes:{' '}
                                          {item.postalCodes.map((pc) => pc.id).join(', ')}
                                        </title>
                                      </circle>
                                    </If>
                                  </Marker>
                                )
                              }
                            })}
                        </Fragment>
                      )
                  })
              }}
            </Geographies>
            {postalCodeData?.topCities
              .sort(({ population1 }, { population2 }) => (population1 > population2 ? -1 : 1))
              .filter((_, index) => index < 10)
              .reverse()
              .map(({ city, lat, long }) => (
                <If key={city} condition={position.zoom > CITY_NAMES_CUT_ZOOM_LESS_THAN}>
                  <Marker coordinates={[long, lat]}>
                    <g>
                      <rect
                        width={calculateTextWidth(city, 'normal 500 1px OpenSans') + 2.5}
                        height="2"
                        x="-0.5"
                        y="-1.3"
                        style={{
                          fill: '#3d3d3d',
                          strokeWidth: 0,
                          borderRadius: '25%',
                          transform: `scale(${10 / position.zoom})`,
                        }}
                        rx="1"
                      />
                      <text
                        x="0.5"
                        fontSize={1}
                        fontFamily="OpenSans"
                        fill="#FFF"
                        style={{ transform: `scale(${10 / position.zoom})` }}
                      >
                        {city}
                      </text>
                    </g>
                  </Marker>
                </If>
              ))}
          </If>
        </ZoomableGroup>
      </ComposableMap>
      <If condition={!hideZoomControls}>
        <div className="st-country-distribution__zoom-controls">
          <div className="st-country-distribution__zoom-controls-1">
            <Icon
              icon="delete"
              className="st-country-distribution__icon st-country-distribution__icon--a45"
              onClick={handleZoomIn}
            />
          </div>
          <div className="st-country-distribution__zoom-controls-2">
            <Icon icon="minus" className="st-country-distribution__icon" onClick={handleZoomOut} />
          </div>
        </div>
      </If>
      <If condition={!geographySelection}>
        <GeoLegend
          className="st-country-distribution__scale"
          calculationMethod={calculationMethod}
          start={legendStart < 1000 ? twoDecimals(legendStart) : format('metric', legendStart)}
          middle={calculationMethod === CALCULATION_METHODS.INDEX ? 1 : undefined}
          end={legendEnd < 1000 ? twoDecimals(legendEnd) : format('metric', legendEnd)}
        />
      </If>
      {/*
      <If condition={!loading && geographySelection}>
        <PostalCodeLegend
          className="st-country-distribution__scale"
          min={minMaxPostalCodeEvents[0] || 1}
          middleMin={(minMaxPostalCodeEvents[1] * 1) / 4}
          middle={(minMaxPostalCodeEvents[1] * 2) / 4}
          middleMax={(minMaxPostalCodeEvents[1] * 3) / 4}
          max={minMaxPostalCodeEvents[1]}
          scale={position.zoom}
        />
      </If>
      */}
      <If condition={rendering || loading}>
        <div className="st-country-distribution__loading-container">
          <CircularLoader className="statistics__loader" />
        </div>
      </If>
      <Modal
        isOpen={
          geographySelection &&
          postalCodeData?.zipCodes &&
          Object.values(postalCodeData?.zipCodes).filter((zc) => zc.id).length === 0
        }
        onClose={onBack}
      >
        <div className="st-country-distribution__no-postal-code-data-modal-content">
          {`We currently don't have data for the country you selected.`}
          <Button onClick={onBack}>Ok</Button>
        </div>
      </Modal>
    </div>
  )
}

export default CountryDistribution
